import { Badge } from '../atoms/Badge/Badge';
import type { BadgeTone } from '../atoms/Badge/Badge.types';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { Alert } from '../atoms/Alert/Alert';
import { PHOTO_MAX_SIZE_BYTES, formatFileSize } from '../atoms/PhotoUploadDropzone/photoFileValidation';
import { useTranslation } from '../../i18n/useTranslation';
import type { PhotoUploadItem, PhotoUploadStatus } from './usePhotoUploadQueue';
import './PhotoUploadQueueList.css';

export interface PhotoUploadQueueListProps {
  items: PhotoUploadItem[];
  busy: boolean;
  onRetry: (id: string) => void;
  onClear: () => void;
  /** Heading level of the list title — 2 on a page, 3 inside a dialog form section. */
  titleLevel?: 2 | 3 | 4;
  /** Prefix of the data-testids (`<prefix>`, `<prefix>-item`, `<prefix>-retry`, …). */
  testIdPrefix?: string;
}

const STATUS_TONE: Record<PhotoUploadStatus, BadgeTone> = {
  queued: 'neutral',
  uploading: 'info',
  done: 'success',
  failed: 'danger',
  rejected: 'danger',
};

/**
 * Per-file status list of a photo upload queue (queued · uploading % · done ·
 * failed + retry · rejected), shared by the gallery and the livestock photos.
 * The texts are the gallery's upload-queue strings (`gallery.uploadQueue.*`).
 * Announcements belong to the surface's own LiveRegion — none here.
 */
export function PhotoUploadQueueList({
  items,
  busy,
  onRetry,
  onClear,
  titleLevel = 2,
  testIdPrefix = 'photo-upload',
}: PhotoUploadQueueListProps) {
  const { t } = useTranslation();
  if (items.length === 0) return null;

  const statusLabel = (item: PhotoUploadItem): string => {
    switch (item.status) {
      case 'queued':
        return t('gallery.uploadQueue.statusQueued');
      case 'uploading':
        return t('gallery.uploadQueue.statusUploading', { percent: item.progress });
      case 'done':
        return t('gallery.uploadQueue.statusDone');
      case 'failed':
        return t('gallery.uploadQueue.statusFailed');
      case 'rejected':
        return t('gallery.uploadQueue.statusRejected');
    }
  };

  const detail = (item: PhotoUploadItem): string | null => {
    if (item.status === 'rejected') {
      return item.rejection === 'size'
        ? t('gallery.uploadQueue.rejectedSize', {
            size: formatFileSize(item.file.size),
            maxMb: PHOTO_MAX_SIZE_BYTES / (1024 * 1024),
          })
        : t('gallery.uploadQueue.rejectedType', {
            type: item.file.type || t('common.photoDropzone.unknownType'),
          });
    }
    if (item.status === 'failed') return t('gallery.uploadQueue.failedDetail');
    return null;
  };

  return (
    <Card className="photo-upload-queue" padding="md" data-testid={`${testIdPrefix}-queue`}>
      <div className="photo-upload-queue__header">
        <Heading level={titleLevel} className="photo-upload-queue__title">
          {t('gallery.uploadQueue.title')}
        </Heading>
        {!busy && (
          <Button variant="ghost" size="sm" onClick={onClear} data-testid={`${testIdPrefix}-clear`}>
            {t('gallery.uploadQueue.clear')}
          </Button>
        )}
      </div>
      <ul className="photo-upload-queue__list">
        {items.map((item) => {
          const info = detail(item);
          const infoId = `${item.id}-detail`;
          return (
            <li
              key={item.id}
              className="photo-upload-queue__item"
              data-testid={`${testIdPrefix}-item`}
              data-status={item.status}
            >
              <div className="photo-upload-queue__row">
                <Text as="span" size="sm" weight="medium" className="photo-upload-queue__name">
                  {item.file.name}
                </Text>
                <Badge tone={STATUS_TONE[item.status]}>{statusLabel(item)}</Badge>
              </div>
              {item.status === 'uploading' && (
                <div
                  className="photo-upload-queue__progress"
                  role="progressbar"
                  aria-label={t('gallery.uploadQueue.progressLabel', { name: item.file.name })}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={item.progress}
                >
                  <div className="photo-upload-queue__progress-fill" style={{ width: `${item.progress}%` }} />
                </div>
              )}
              {info !== null && (
                <div className="photo-upload-queue__row">
                  {/* Announced once by the surface's upload announcer — no second live region here. */}
                  <Alert id={infoId} live="off" compact className="photo-upload-queue__detail">
                    {info}
                  </Alert>
                  {item.status === 'failed' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onRetry(item.id)}
                      aria-label={t('gallery.uploadQueue.retryLabel', { name: item.file.name })}
                      aria-describedby={infoId}
                      data-testid={`${testIdPrefix}-retry`}
                    >
                      {t('gallery.uploadQueue.retry')}
                    </Button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
